import { LiveKitRoom, RoomAudioRenderer, useRoomContext } from '@livekit/components-react'
import '@livekit/components-styles'
import '@livekit/components-styles/prefabs'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { RemoteParticipant } from 'livekit-client'

import { logger } from '@/services/Logger'
import MusiversalGridLayout from './internal/MusiversalGridLayout'
import MuseGridLayout from './internal/MuseGridLayout'
import HostLayout from './internal/HostLayout'
import './internal/egress.css'
import useEgressProAudio from './internal/useEgressProAudio'
import { AudioEngineRelayServer } from '@/core/AudioEngine'
import FuzzyGridLayout from './internal/FuzzyGridLayout'
import { LiveKitMetadata } from '@/services/RoomContextMusePeer'
import { defaultConsole } from '@/DefaultConsole'
import { SpeakerLayout } from './internal/SpeakerLayout'

const log = logger.createChildLogger({ name: 'egress-renderer' })

function useQuery() {
    const { search } = useLocation()
    return useMemo(() => new URLSearchParams(search), [search])
}

export default function EgressPage() {
    const q = useQuery()
    let url = atob(q.get('url') ?? '')
    let token = q.get('token') ?? ''
    let layout = q.get('layout') ?? ''
    let relay = null
    try {
        relay = JSON.parse(atob(q.get('relay') ?? ''))
    } catch (err) {
        log.error('EgressPage', 'Failed to parse relay from url')
    }

    const [error, setError] = useState<Error | null>(null)

    if (!url || !token) {
        return <div className="text-white">missing required params url and token</div>
    }

    return (
        <LiveKitRoom serverUrl={url} token={token} onError={setError}>
            {error ? (
                <div className="text-white">{error.message}</div>
            ) : (
                <CompositeTemplate layout={layout} relay={relay} />
            )}
        </LiveKitRoom>
    )
}

interface CompositeTemplateProps {
    layout: string
    relay: AudioEngineRelayServer | null
}

function CompositeTemplate({ layout, relay }: CompositeTemplateProps) {
    const room = useRoomContext()
    const { aeRoom, handleMetadata: handleMetadataForProAudio } = useEgressProAudio()
    const [connected, setConnected] = useState<boolean>(false)
    const [galleryOrdering, setGalleryOrdering] = useState<string[] | null>()
    const [hiddenParticipantIds, setHiddenParticipantIds] = useState<string[]>([])

    const handleMetadata = (p: RemoteParticipant, leaving?: boolean) => {
        const metadata = parseMetadata(p)
        if (metadata) {
            handleMetadataForProAudio(p, metadata, leaving)
        }
        if (metadata?.manifest?.galleryClientOrdering) {
            setGalleryOrdering(metadata?.manifest.galleryClientOrdering)
        }
        if (metadata?.manifest?.videoHiddenSessionIds) {
            setHiddenParticipantIds(metadata?.manifest.videoHiddenSessionIds)
        }
    }

    useEffect(() => {
        const h = async () => {
            defaultConsole.log('START_RECORDING')
            log.verbose('CompositeTemplate', `connected`)
            const [roomId, _sessionId] = room.name.split(':')

            const fallbackRelay: AudioEngineRelayServer = {
                code: 'ussf-01',
                name: 'San Francisco',
                websocketUrl: 'wss://relay-ussf-01.musecore.net',
                url: '',
            }

            aeRoom.current.joinRoom({
                roomUrn: roomId,
                servers: [relay ?? fallbackRelay],
                userInfo: {
                    authToken: '',
                    username: '__EGRESS_BOT__',
                    userUrn: '__EGRESS_BOT__',
                },
            })
            setConnected(true)

            // Already connected participants
            for (const [id, p] of room.remoteParticipants) {
                log.verbose('CompositeTemplate', 'attaching metadata change handler', { id })
                handleMetadata(p)
                p.on('participantMetadataChanged', () => {
                    handleMetadata(p)
                    log.verbose('CompositeTemplate', 'new participant metadata', { id: p.sid, metadata: p.metadata })
                })
            }

            // New participants
            room.on('participantConnected', (p) => {
                log.verbose('CompositeTemplate', `new participant`, { id: p.sid, metadata: p.metadata })
                handleMetadata(p)
                p.on('participantMetadataChanged', () => {
                    handleMetadata(p)
                    log.verbose('CompositeTemplate', 'new participant metadata', { id: p.sid, metadata: p.metadata })
                })
            })

            // Leaving participants
            room.on('participantDisconnected', (p) => {
                log.verbose('CompositeTemplate', `participant disconnected`, { id: p.sid })
                handleMetadata(p, true)
            })
        }
        room.on('connected', h)
        return () => {
            room.off('connected', h)
        }
    }, [])

    useEffect(() => {
        if (room) {
            let hasTrack = false
            for (const p of Array.from(room.remoteParticipants.values())) {
                if (p.trackPublications.size > 0) {
                    hasTrack = true
                    break
                }
            }
        }
    }, [room])
    if (!connected) {
        return <div className="roomContainer dark"> not connected </div>
    }

    let layoutView: ReactElement = <div className="roomContainer dark">unknown layout</div>
    if (layout.startsWith('host-')) {
        const hostId = layout.substring(5)
        layoutView = <HostLayout room={room} hostUserId={hostId} />
    } else if (layout.startsWith('musiversal-grid-')) {
        const hostId = layout.substring(16)
        layoutView = <MusiversalGridLayout room={room} hostUserId={hostId} />
    } else if (layout === 'fuzzy-grid') {
        layoutView = (
            <FuzzyGridLayout
                room={room}
                galleryOrdering={galleryOrdering}
                hiddenParticipantIds={hiddenParticipantIds}
            />
        )
    } else if (layout === 'grid') {
        layoutView = <MuseGridLayout room={room} hostUserId="" />
    } else if (layout === 'speaker') {
        layoutView = <SpeakerLayout room={room} />
    }

    return (
        <div className="roomContainer dark">
            {layoutView}
            <RoomAudioRenderer />
        </div>
    )
}

function parseMetadata(p: RemoteParticipant): LiveKitMetadata | null {
    if (p.metadata) {
        try {
            let metadata = JSON.parse(p.metadata) as LiveKitMetadata
            return metadata
        } catch (e) {
            log.error('parseMetadata', 'failed to apply manifest', { err: e, user: p.identity })
        }
    }

    return null
}
